<script setup lang="ts">
import type { MessageSchema } from '@/i18n';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n<{ message: MessageSchema }>({ useScope: 'global' });

interface Props {
  /**
   * Button type according to Apple guidelines
   * - 'sign-in': "Sign in with Apple"
   * - 'continue': "Continue with Apple"
   * - 'sign-up': "Sign up with Apple"
   */
  type?: 'sign-in' | 'continue' | 'sign-up';

  /**
   * Corner radius style
   * - 'default': Standard rounded corners
   * - 'square': 90-degree corners
   * - 'capsule': Maximum rounded (pill shape)
   */
  cornerRadius?: 'default' | 'square' | 'capsule';

  /**
   * Button color scheme
   * - 'black': Black button with white text (default)
   * - 'white': White button with black text and border
   */
  theme?: 'black' | 'white';

  /**
   * Loading state
   */
  loading?: boolean;

  /**
   * Disabled state
   */
  disabled?: boolean;

  /**
   * Full width button
   */
  fullWidth?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  type: 'sign-in',
  cornerRadius: 'default',
  theme: 'white',
  loading: false,
  disabled: false,
  fullWidth: true,
});

const emit = defineEmits<{
  click: [];
}>();

const buttonText = computed(() => {
  switch (props.type) {
    case 'continue':
      return t('auth.registration.continueWithApple', 'Continue with Apple');
    case 'sign-up':
      return t('auth.registration.signUpWithApple', 'Sign up with Apple');
    case 'sign-in':
    default:
      return t('auth.login.signInWithApple', 'Sign in with Apple');
  }
});

const cornerRadiusClass = computed(() => {
  switch (props.cornerRadius) {
    case 'square':
      return 'rounded-none';
    case 'capsule':
      return 'rounded-full';
    case 'default':
    default:
      return 'rounded-md';
  }
});

const themeClasses = computed(() => {
  if (props.theme === 'white') {
    return 'bg-white text-black border border-gray-300 hover:bg-gray-50';
  }
  return 'bg-black text-white hover:bg-gray-900';
});

const handleClick = () => {
  if (!props.loading && !props.disabled) {
    emit('click');
  }
};
</script>

<template>
  <button
    type="button"
    :class="[
      'apple-signin-button',
      'flex items-center justify-center gap-2',
      'px-4 py-3',
      'text-base font-medium',
      'transition-colors duration-200',
      'focus:outline-none focus:ring-2 focus:ring-offset-2',
      cornerRadiusClass,
      themeClasses,
      {
        'w-72': fullWidth,
        'min-w-[140px]': !fullWidth,
        'cursor-not-allowed opacity-60': disabled || loading,
        'focus:ring-black': theme === 'black',
        'focus:ring-gray-400': theme === 'white',
      },
    ]"
    :disabled="disabled || loading"
    @click="handleClick"
  >
    <!-- Apple Icon SVG (official Apple logo) -->
    <!-- <svg  xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24"
      fill="currentColor" class="flex-shrink-0">
      <path
        d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
    </svg> -->
    <i v-if="!loading" class="pi pi-apple"></i>

    <i v-else class="pi-spinner pi-spin"></i>
    <!-- Loading Spinner -->
    <!-- <svg v-else class="animate-spin h-5 w-5 flex-shrink-0" xmlns="http://www.w3.org/2000/svg" fill="none"
      viewBox="0 0 24 24">
      <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
      <path class="opacity-75" fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z">
      </path>
    </svg> -->

    <span class="flex-1 text-center">{{ buttonText }}</span>
  </button>
</template>

<style scoped>
/* Ensure minimum height of 30pt (40px) as per Apple guidelines */
.apple-signin-button {
  min-height: 40px;
  height: 44px;
}

/* Ensure minimum margin equivalent to 1/10 of button height */
.apple-signin-button {
  margin: 4px 0;
}

/* Font family: SF Pro on Apple devices, fallback to app's Sora font */
.apple-signin-button {
  font-family: -apple-system, BlinkMacSystemFont, 'SF Pro Text', 'SF Pro Display', 'Sora', sans-serif;
}
</style>
